import { zodResolver } from '@hookform/resolvers/zod';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Autocomplete,
  useTheme,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Typography,
  Alert,
  Box,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { type FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Frequency, Frequency as RRuleFrequency, RRule } from 'rrule';

import { CheckInDurations, CheckInIntervals, type Day, DAYS, FREQUENCIES, RULE_TYPES, UPDATE_TYPES } from './constants.js';
import { MonitoringHoursType } from '../../models/index.js';
import { type MonitoringHoursRuleForm, UpdateType } from '../../types/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import { type MonitoringHoursFormValues } from '../Forms/formTypes.js';
import { monitoringHoursFormSchema } from '../Forms/validations.js';

const defaultValues: MonitoringHoursFormValues = {
  start: DateTime.now(),
  end: DateTime.now().plus({ hour: 1 }),
  repeats: false,
  freq: null,
  interval: null,
  byDay: null,
  until: null,
  type: MonitoringHoursType.MONITORING,
  checkInInterval: null,
  checkInDuration: null,
  updateType: UpdateType.RULE,
};

export type MonitoringHoursDialogMode = 'create' | 'occurrence' | 'series';

interface MonitoringHoursFormProps {
  open: boolean;
  onClose: () => void;
  rule?: MonitoringHoursRuleForm;
  mode?: MonitoringHoursDialogMode;
  timeZone?: string;
  refetch: () => void;
  onSubmit: (form: MonitoringHoursRuleForm, callback: () => void) => void;
  onDelete: (form: MonitoringHoursRuleForm, callback: () => void) => void;
}

const MonitoringHoursForm: FC<MonitoringHoursFormProps> = ({
                                                             open,
                                                             rule,
                                                             mode = 'create',
                                                             timeZone,
                                                             refetch,
                                                             onSubmit,
                                                             onDelete,
                                                             ...props
                                                           }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isSeries = mode === 'series';
  const isEditing = mode !== 'create';
  const values = useMemo(
    () => ({
      start: rule?.start ? DateTime.fromJSDate(rule.start) : DateTime.now(),
      end: rule?.end ? DateTime.fromJSDate(rule.end) : DateTime.now().plus({hour: 1}),
      repeats: rule?.repeats || false,
      freq: FREQUENCIES.find((frequency) => frequency.value === rule?.freq)?.value ?? null,
      byDay: rule?.byDay ? DAYS.filter((day) => rule.byDay?.includes(day.value)) : ([] as Day[]),
      interval: rule?.interval || 1,
      until: rule?.until ? DateTime.fromJSDate(rule.until) : null,
      type: rule?.type || MonitoringHoursType.MONITORING,
      checkInInterval: rule?.checkInInterval || 4,
      checkInDuration: rule?.checkInDuration || 30,
      updateType: rule?.updateType || UpdateType.RULE,
    }),
    [rule],
  );
  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    clearErrors,
    formState: {errors, isDirty},
  } = useForm({
    defaultValues,
    values,
    resolver: zodResolver(monitoringHoursFormSchema),
  });
  const [repeats, freq, type, start, end, interval, byDay, until, checkInDuration, checkInInterval] = useWatch({
    control,
    name: [
      'repeats',
      'freq',
      'type',
      'start',
      'end',
      'interval',
      'byDay',
      'until',
      'checkInDuration',
      'checkInInterval',
    ],
  });
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const theme = useTheme();

  const repeatExplanation = useMemo(() => {
    if (freq) {
      const rule = new RRule({
        freq: freq,
        interval: interval ?? undefined,
        byweekday: byDay?.length ? byDay.map((day: Day) => day.value) : ([] as number[]),
        dtstart: start ? start.toJSDate() : null,
        until: until ? until.toJSDate() : null,
      });
      return `Repeats ${rule.toText()}`;
    }
    return 'Please select a frequency.';
  }, [freq, interval, byDay, start, until]);

  const checkInCadenceExplanation = useMemo(() => {
    if (!checkInDuration || !checkInInterval || !start || !end) {
      return 'Pick a duration and how often check-ins repeat inside the window.';
    }
    const windowHours = end.diff(start, 'hours').hours;
    const checkInCount = Math.max(1, Math.floor(windowHours / checkInInterval));
    const cadence = checkInInterval === 1 ? 'hour' : `${checkInInterval} hours`;
    return `A ${checkInDuration}-minute check-in runs every ${cadence} inside this window — ${checkInCount} ${
      checkInCount === 1 ? 'check-in' : 'check-ins'
    } per window.`;
  }, [checkInDuration, checkInInterval, start, end]);

  const setUntouchedValues = () => {
    if (rule) {
      reset(values);
    }
  };
  useEffect(() => {
    if (isEditing) {
      setUntouchedValues();
    }
  }, [isEditing]);

  const onClose = () => {
    setDiscardConfirmOpen(false);
    props.onClose();
    setTimeout(() => reset(defaultValues), theme.transitions.duration.leavingScreen);
  };

  const requestClose = () => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleDataSubmit = (data: MonitoringHoursFormValues) => ({
    ...data,
    start: data.start.toJSDate(),
    end: data.end.toJSDate(),
    until: data?.until?.toJSDate(),
    freq: data.freq ?? undefined,
    interval: data.interval ?? undefined,
    checkInInterval: data.checkInInterval ?? undefined,
    checkInDuration: data.checkInDuration ?? undefined,
    byDay: data.byDay?.length ? data.byDay.map((day) => day.value) : ([] as number[]),
    id: rule?.id,
    eventId: rule?.eventId,
  });
  return (
    <>
    <Dialog
      open={open}
      onClose={requestClose}
      PaperProps={{
        component: 'form',
        sx: {minWidth: 450},
        onSubmit: handleSubmit((data: MonitoringHoursFormValues) => {
          setIsSubmitting(true);
          onSubmit(handleDataSubmit(data), () => {
            refetch();
            onClose();
            setTimeout(() => setIsSubmitting(false), theme.transitions.duration.leavingScreen);
          });
        }),
      }}
    >
      <DialogTitle>
        {isSeries ? 'Edit monitoring rule' : isEditing ? 'Update Event info' : 'Add a new Event'}
        {isEditing ? (
          <IconButton onClick={setUntouchedValues} disabled={!isDirty}>
            <RestartAltIcon/>
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{mt: 2}}>
          <Controller
            name={'type'}
            control={control}
            render={({field}) => (
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={field.value}
                onChange={(_event, value: MonitoringHoursType | null) => value && field.onChange(value)}
              >
                {RULE_TYPES.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
          <Typography variant={'caption'} color={'text.secondary'}>
            {type === MonitoringHoursType.CHECK_IN
              ? 'Short scheduled check-ins with AI captures, instead of continuous monitoring.'
              : 'Continuous monitoring for the whole window.'}
          </Typography>
          {isSeries ? (
            <>
              <Stack spacing={0.25}>
                <Typography variant={'caption'} color={'text.secondary'}>
                  Rule started on
                </Typography>
                <Typography variant={'body1'}>
                  {rule?.ruleStartDate
                    ? DateTime.fromISO(rule.ruleStartDate, {zone: timeZone || 'local'}).toFormat('LLL d, yyyy')
                    : 'Unknown'}
                </Typography>
              </Stack>
              <Controller
                name={'start'}
                control={control}
                render={({field}) => (
                  <DatePicker
                    value={field.value}
                    onChange={(value: DateTime | null) => {
                      if (!value) {
                        return;
                      }
                      const currentStart = field.value;
                      const currentEnd = getValues('end');
                      // Overnight windows end on the following day; keep that offset when the
                      // rule is moved to a different start date.
                      const dayOffset = Math.round(
                        currentEnd.startOf('day').diff(currentStart.startOf('day'), 'days').days,
                      );
                      const nextStart = currentStart.set({year: value.year, month: value.month, day: value.day});
                      field.onChange(nextStart);
                      setValue(
                        'end',
                        currentEnd
                          .set({year: nextStart.year, month: nextStart.month, day: nextStart.day})
                          .plus({days: dayOffset}),
                        {shouldDirty: true},
                      );
                    }}
                    slotProps={{
                      textField: {
                        InputLabelProps: {required: true},
                        helperText: errors?.start?.message || 'Occurrences before this date keep their current times.',
                        fullWidth: true,
                      },
                    }}
                    label={'Apply changes from'}
                  />
                )}
              />
              <Stack spacing={2} direction={'row'}>
                <Controller
                  name={'start'}
                  control={control}
                  render={({field}) => (
                    <TimePicker
                      {...field}
                      slotProps={{
                        textField: {
                          InputLabelProps: {required: true},
                          helperText: '',
                          fullWidth: true,
                        },
                      }}
                      label={'Daily start'}
                    />
                  )}
                />
                <Controller
                  name={'end'}
                  control={control}
                  render={({field}) => (
                    <TimePicker
                      {...field}
                      slotProps={{
                        textField: {
                          InputLabelProps: {required: true},
                          helperText: errors?.end?.message || '',
                          fullWidth: true,
                        },
                      }}
                      label={'Daily end'}
                    />
                  )}
                />
              </Stack>
            </>
          ) : (
          <Stack spacing={2} direction={'row'}>
            <Controller
              name={'start'}
              control={control}
              render={({field}) => (
                <DateTimePicker
                  {...field}
                  minDateTime={DateTime.now().minus({days: 31})}
                  slotProps={{
                    textField: {
                      InputLabelProps: {required: true},
                      helperText: errors?.start?.message || '',
                      fullWidth: true,
                    },
                  }}
                  label={'Event start'}
                />
              )}
            />
            <Controller
              name={'end'}
              control={control}
              render={({field}) => (
                <DateTimePicker
                  {...field}
                  minDateTime={start}
                  slotProps={{
                    textField: {
                      InputLabelProps: {required: true},
                      helperText: errors?.end?.message || '',
                      fullWidth: true,
                    },
                  }}
                  onError={(_) => {
                    if (_) {
                      setError('end', {type: 'custom', message: 'End time must be after start time'});
                    } else {
                      clearErrors('end');
                    }
                  }}
                  label={'Event end'}
                />
              )}
            />
          </Stack>
          )}
          {type === MonitoringHoursType.CHECK_IN ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'secondary.main',
                backgroundColor: (currentTheme) =>
                  currentTheme.palette.mode === 'light' ? 'rgba(245,127,60,0.06)' : 'rgba(255,155,94,0.08)',
              }}
            >
              <Typography variant={'overline'} color={'secondary.main'}>
                Check-in cadence
              </Typography>
              <Stack spacing={2} direction={'row'} sx={{mt: 1.5}}>
                <Controller
                  name={'checkInDuration'}
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      error={!!errors?.checkInDuration}
                      helperText={errors?.checkInDuration?.message || ''}
                      InputLabelProps={{required: true}}
                      label={'Check-in duration'}
                      select
                      fullWidth
                    >
                      {CheckInDurations.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option} minutes
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name={'checkInInterval'}
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      error={!!errors?.checkInInterval}
                      helperText={errors?.checkInInterval?.message || ''}
                      InputLabelProps={{required: true}}
                      label={'Every'}
                      select
                      fullWidth
                    >
                      {CheckInIntervals.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option} {option === 1 ? 'hour' : 'hours'}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Stack>
              <Typography variant={'caption'} color={'text.secondary'} sx={{display: 'block', mt: 1.5}}>
                {checkInCadenceExplanation}
              </Typography>
            </Box>
          ) : null}
          <Divider/>
          <Typography>Repetition</Typography>
          <Controller
            name={'repeats'}
            control={control}
            render={({field}) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                  />
                }
                label="Recurring"
              />
            )}
          />
          {repeats ? (
            <>
              <Stack spacing={2} direction={'row'}>
                <Controller
                  name={'freq'}
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      error={!!errors?.freq}
                      helperText={errors?.freq?.message || ''}
                      InputLabelProps={{required: true}}
                      label={'Frequency'}
                      select
                      fullWidth
                    >
                      {FREQUENCIES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name={'interval'}
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      InputLabelProps={{required: true}}
                      type={'number'}
                      sx={{
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                      }}
                      error={!!errors?.interval}
                      helperText={errors?.interval?.message || ''}
                      label={'Interval'}
                      fullWidth
                    />
                  )}
                />
              </Stack>
              {freq === RRule.WEEKLY ? (
                <Controller
                  name={'byDay'}
                  control={control}
                  render={({field}) => {
                    return (
                      <Autocomplete
                        value={field.value ?? undefined}
                        onChange={(_: unknown, value: Day[]) => field.onChange(value)}
                        id={'byDay-select'}
                        options={DAYS}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => <TextField {...params} label={'Days of the week'}/>}
                        multiple
                        key={'byDay'}
                        renderOption={({key, ...props}, option, {inputValue}) => (
                          <AutocompleteOptions option={option.label} value={inputValue} key={key} {...props} />
                        )}
                      />
                    );
                  }}
                />
              ) : null}
              <Controller
                name={'until'}
                control={control}
                render={({field}) => (
                  <DatePicker
                    {...field}
                    minDate={end}
                    slotProps={{
                      textField: {
                        InputLabelProps: {required: true},
                        helperText: errors?.until?.message || '',
                      },
                    }}
                    onError={(_) => {
                      if (_) {
                        setError('until', {type: 'custom', message: 'Recurring date must be after end date'});
                      } else {
                        clearErrors('end');
                      }
                    }}
                    label={'Recurring until'}
                  />
                )}
              />
            </>
          ) : null}
          {mode === 'occurrence' ? (
            <>
              <Divider/>
              <Typography>Apply changes to</Typography>
              <Controller
                name={'updateType'}
                control={control}
                render={({field}) => (
                  <RadioGroup {...field}>
                    {UPDATE_TYPES.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </>
          ) : null}
        </Stack>
        <Alert severity="info" variant="outlined" sx={{mt: 2, alignItems: 'center'}}>
          {repeats ? repeatExplanation : "Doesn't repeat."}
        </Alert>
      </DialogContent>
      <DialogActions>
        {isEditing && rule?.id ? (
          <LoadingButton
            loading={isDeleting}
            variant={'contained'}
            color={'error'}
            sx={{mr: 'auto'}}
            onClick={
              handleSubmit((data) => {
                setIsDeleting(true);
                onDelete(handleDataSubmit(data), () => {
                  refetch();
                  onClose();
                  setTimeout(() => setIsDeleting(false), theme.transitions.duration.leavingScreen);
                });
              })
            }
          >
            Delete
          </LoadingButton>
        ) : null}
        <Button onClick={requestClose}>Cancel</Button>
        <LoadingButton type={'submit'} loading={isSubmitting} variant={'contained'} disabled={!_.isEmpty(errors)}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
    <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)}>
      <DialogTitle>Discard changes?</DialogTitle>
      <DialogContent>
        <Typography variant={'body2'} color={'text.secondary'}>
          This rule has unsaved edits. If you close now, they will be lost.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDiscardConfirmOpen(false)}>Keep editing</Button>
        <Button color={'error'} onClick={onClose}>
          Discard
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default MonitoringHoursForm;
